import Element from "../element";
import ServiceLocator from "../../../global/service-locator";
import Api from "../../../lib/api";

import SubmitButton from "./submit-button";
import StepsModule from "./steps-module";
import FieldGroup from "./field-group";

import FormField from "./form-field";
import TextField from "./fields/text-field";
import SelectField from "./fields/select-field";
import CheckboxesField from "./fields/checkboxes-field";
import TelField from "./fields/tel-field";
import CourseField from "./fields/course-field";
import ConsultationField from "./fields/consultation-field";
import AgreementField from "./fields/agreement-field";
import CityField from "./fields/city-field";
import CoachesField from "./fields/coaches-field";
import RatingField from "./fields/rating-field";
import SocialField from "./fields/social-field";
import PhotoField from "./fields/photo-field";
import SwitchField from "./fields/switch-field";
import RadioField from "./fields/radio-field";
import FileField from "./fields/file-field";
import ButtonsField from "./fields/buttons-field";

export default class Form extends Element {
	constructor(props) {
		super(props);

		/* Config */
		this.layout = props.layout || 'classic'; // $root data-layout attr overlaps other values
		this.shouldClose = props.shouldClose == null ? true : props.shouldClose;
		this.waitResponse = props.waitResponse == null ? false : props.waitResponse;
		this.showResult = props.showResult == null ? true : props.showResult;
		this.shouldChangeHeight = props.shouldChangeHeight == null ? false : props.shouldChangeHeight;
		this.resetAfterSubmit = props.resetAfterSubmit == null ? true : props.resetAfterSubmit;
		this.cacheEnabled = props.cacheEnabled == null ? true : props.cacheEnabled;

		/* Fields Options */
		this.fieldsOptions = props.fieldsOptions || [];

		/* Submit Config */
		this.submitConf = Object.assign({
			animation: true
		}, (props.submitConf || {}));


		this.formId = props.formId;
		this.uri = props.uri || null;
		this.baseUrl = props.baseUrl || false;
		this.subscriptions = props.subscriptions || [];
		this.defaultData = {
			cid: '',
			fid: '',
			lang: document.querySelector('html').lang
		};
		this.data = {};
		this.initialValues = {};

		/* Submit Completion Stats */
		this.submitStatus = null;
		this.submitResponse = null;

		this.fields = [];
		this.externalFields = props.fields || [];
		this.fieldGroups = props.fieldGroups || [];
		this.submitButton = props.submitButton;
		this.secondarySubmitButton = props.secondarySubmitButton;
		this.screens = props.screens || []; // @type { name: string, $element: HtmlElement }[]

		this.AFTER_INIT_EVENT = 'afterInit';
		this.BEFORE_SUBMIT_EVENT = 'beforeSubmit';
		this.SUBMIT_COMPLETE_EVENT = 'submitComplete';
		this.SUBMIT_SUCCESS_EVENT = 'submitSuccess';
		this.SUBMIT_FAILURE_EVENT = 'submitFailure';
		this.SUBMIT_FINISH_EVENT = 'submitFinish';
		this.DATA_SENT_EVENT = 'dataSent';
		this.FIELD_VALUE_CHANGE_EVENT = 'valueChange';

		this.serviceLocator = ServiceLocator.instance;

		this.handleSubmit = this.handleSubmit.bind(this);
		this.handleSecondarySubmit = this.handleSecondarySubmit.bind(this);

		this.init();
	}

	init() {
		this.beforeInit();
		this.initComponents();
		this.makeInitialChecks();
		this.setFidCid();
		this.validate();

		super.init();
		this.dispatchEvent(this.AFTER_INIT_EVENT, this);
	}

	beforeInit() {
		if (!this.formId) {
			throw new Error('Form: Form Id is not defined!');
		}

		this.$root = document.getElementById(`form-${this.formId}`);

		if (!this.$root) {
			throw new Error('Form: Form Name is wrong or appropriate element does not exist!');
		}

		this.$formLayout = this.$root.querySelector('.form_layout');

		this.setDefaultData();
		this.initScreens();

		if (this.props.data) {
			this.defaultData = Object.assign(this.defaultData, this.props.data);
		}

		if (this.props.initialValues && typeof this.props.initialValues === 'object') {
			this.initialValues = this.props.initialValues;
		}

		if (this.$root.dataset.layout) {
			this.layout = this.$root.dataset.layout;
		}

		this.$fields = this.$root.querySelectorAll('.form-field');
	}

	setDefaultData() {
		this.defaultData = Api.updateAndReturnDataWithUtmTags(this.defaultData);
	}

	initScreens() {
		const $screens = this.$root.querySelectorAll('.form_screen');

		this.screens = Array.from($screens).map(($screen) => {
			const screenName = $screen.dataset.name;
			const screenConfig = this.screens.find((screen) => screen.name === screenName) || {
				name: screenName
			};
			Object.assign(screenConfig, { $element: $screen });
			return screenConfig;
		});
	}

	initComponents() {
		/* Submit Button */
		const $submitButton = this.$root.querySelector('button[type="submit"]');

		if ($submitButton) {
			this.submitButton = this.submitButton || new SubmitButton({
				$root: $submitButton
			});
		}

		if (this.secondarySubmitButton) {
			this.secondarySubmitButton.subscribe('click', this.handleSecondarySubmit);
		}

		/* Agreement Checkbox */
		const $agreementCheckbox = this.$root.querySelector('.form_agreement');

		if ($agreementCheckbox) {
			this.fields.push(new AgreementField({
				$root: $agreementCheckbox,
				name: 'agreement',
				required: true
			}))
		}

		/* External field modules initialization (instances of FormField passed to form class as params) */
		if (Array.isArray(this.externalFields) && this.externalFields.length) {
			this.externalFields.forEach((externalField) => {
				if (externalField instanceof FormField) {
					this.fields.push(externalField);
				}
			});
		}

		/* Field modules initialization */
		this.$fields.forEach(($field) => {
			try {
				if ($field.dataset.custom && $field.dataset.custom === 'true') {
					this.initCustomFields($field);
				} else {
					this.initPredefinedField($field);
				}
			} catch (err) {
				console.error(`Form (${this.formId})/ Field (${$field.id}):`, err);
			}
		});

		/* Set Initial Values */
		this.setInitialValues();

		/* Field Groups */
		if (Array.isArray(this.fieldGroups) && this.fieldGroups.length) {
			this.initFieldGroups();
		}

		/* Steps Module */
		if (this.$root.dataset.steps) {
			this.stepsModule = new StepsModule(
				this.$root,
				this.$formLayout,
				this.fields
			);
		}
	}

	setInitialValues() {
		let storage = costorage.get('h_formValues');

		if (storage) {
			storage = JSON.parse(storage);
		}

		this.fields.forEach((field) => {
			const name = field.name;
			const predefinedValue = this.initialValues[name];
			const valueFromCache = storage ? storage[name] : null;

			if (predefinedValue) {
				/* Set values from props */
				field.setInitialValue(predefinedValue);
			} else if (this.cacheEnabled && field.cacheable && valueFromCache) {
				/* Set Cached Values */
				field.setInitialValue(valueFromCache);
			}
		});
	}

	initCustomFields($field) {
		const $input = $field.querySelector('[data-input]');

		if (!$input) {
			throw new Error('Form: Initialization of custom field failed. Input does not exist or data-input attribute is not defined');
		}

		const fieldType = $field.dataset.type;
		const fieldName = $field.dataset.name;
		const fieldConfig = {
			$root: $field,
			name: fieldName
		};

		const fieldUserOptions = this.fieldsOptions.find(({ name }) => name === fieldName);

		if (fieldUserOptions) {
			Object.assign(fieldConfig, fieldUserOptions);
		}

		switch (fieldType) {
			case 'switch': {
				this.fields.push(new SwitchField(fieldConfig))
				break;
			}
			default: {
				this.initDefaultField($field);
				break;
			}
		}
	}

	initPredefinedField($field) {
		const fieldId = $field.id;

		switch (fieldId) {

			/* Email Field */
			case `field-email-${this.formId}`: {
				this.fields.push(new TextField({
					$root: $field,
					name: 'email',
					pattern: [
						/^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
						/^((?!mark@gmail\.com).)*$/
					],
					cacheable: true
				}));

				break;
			}

			/* Tel Field */
			case `field-tel-${this.formId}`: {
				this.fields.push(new TelField({
					$root: $field,
					name: 'tel',
					cacheable: true
				}));

				break;
			}

			/* Course Field */
			case `field-course-${this.formId}`: {
				this.fields.push(new CourseField({
					$root: $field,
					name: 'course'
				}));

				break;
			}

			/* City Field */
			case `field-city-${this.formId}`: {
				this.fields.push(new CityField({
					$root: $field,
					name: 'city'
				}));

				break;
			}

			/* Messenger Field */
			case `field-messenger-${this.formId}`: {
				this.fields.push(new SocialField({
					$root: $field,
					name: 'messenger'
				}));

				break;
			}

			/* Get Consultation Field */
			case `field-consultation-${this.formId}`: {
				if (this.defaultData.type == null) {
					throw new Error('Form: Get consultation field has been defined but form type data isn\'t set!');
				}

				this.fields.push(new ConsultationField({
					$root: $field,
					name: 'getConsultation'
				}));

				break;
			}

			/* Coaches Field */
			case `field-coaches-${this.formId}`: {
				this.fields.push(new CoachesField({
					$root: $field,
					name: 'coaches'
				}));

				break;
			}

			/* Rating Field */
			case `field-rating-${this.formId}`: {
				this.fields.push(new RatingField({
					$root: $field,
					name: 'rating'
				}));

				break;
			}

			/* Socials Field */
			case `field-social-${this.formId}`: {
				this.fields.push(new SocialField({
					$root: $field,
					name: 'social'
				}));

				break;
			}

			/* Photo Field */
			case `field-photo-${this.formId}`: {
				this.fields.push(new PhotoField({
					$root: $field,
					name: 'photo'
				}));

				break;
			}

			/* Standard Fields */
			default: {
				const alreadyExists = this.fields.find((field) => {
					return $field.id === field.$root.id;
				});

				if (!alreadyExists) {
					this.initDefaultField($field);
				}
			}
		}
	}

	initDefaultTextField($field) {
		let pattern = /^[A-Za-zА-Яа-яЁёЇїІіЄєҐґ\s']+['`ʼ\-]?[A-Za-zА-Яа-яЁёЇїІіЄєҐґ\s']+$/;

		if ($field.querySelector('[data-input="textarea"]')) {
			pattern = /.{2,}/;
		}

		if ($field.dataset.pattern) {
			pattern = new RegExp($field.dataset.pattern);
		}

		const fieldName = $field.dataset.name;
		const fieldConfig = {
			$root: $field,
			pattern
		};

		const fieldUserOptions = this.fieldsOptions.find(({ name }) => name === fieldName);

		if (fieldUserOptions) {
			Object.assign(fieldConfig, fieldUserOptions);
		}

		this.fields.push(new TextField(fieldConfig));
	}

	initDefaultField($field) {
		const fieldType = $field.dataset.type || 'textInput';
		const fieldName = $field.dataset.name;
		const fieldConfig = {
			$root: $field,
			name: $field.dataset.name
		};

		const fieldUserOptions = this.fieldsOptions.find(({ name }) => name === fieldName);

		if (fieldUserOptions) {
			Object.assign(fieldConfig, fieldUserOptions);
		}

		switch (fieldType) {
			case 'switch': {
				this.fields.push(new SwitchField(fieldConfig));
				break;
			}
			case 'select': {
				this.fields.push(new SelectField(fieldConfig));
				break;
			}
			case 'checkboxes': {
				Object.assign(fieldConfig, { altDataType: $field.dataset.datatype && $field.dataset.datatype === 'alt' });
				this.fields.push(new CheckboxesField(fieldConfig));
				break;
			}
			case 'radio': {
				this.fields.push(new RadioField(fieldConfig));
				break;
			}
			case 'file': {
				this.fields.push(new FileField(fieldConfig));
				break;
			}
			case 'buttons': {
				this.fields.push(new ButtonsField(fieldConfig));
				break;
			}
			default: {
				this.initDefaultTextField($field);
				break;
			}
		}
	}

	initFieldGroups() {
		if (!Array.isArray(this.fieldGroups) || !this.fieldGroups.length) return;

		this.fieldGroups.forEach(({ fields, Controller, options = {} }) => {
			if (fields == null ||
				Controller == null ||
				!Array.isArray(fields) ||
				!(FieldGroup.isPrototypeOf(Controller))
			) {
				throw new Error('Form: Required props are not defined or have wrong types in fieldGroups objects');
			}

			const formFields = {};

			fields.forEach((fieldName) => {
				formFields[fieldName] = this.fields.find((field) => field.name === fieldName);
			});

			if (Object.keys(this.initialValues).length) {
				Object.assign(options, { initialValues: this.initialValues });
			}

			Object.assign(options, {
				formId: this.formId,
				subscriptions: [
					{
						event: 'forceFormValidation',
						callback: () => {
							this.validate();
						}
					}
				]
			});

			const fieldGroupController = new Controller(formFields, options);
		});
	}

	makeInitialChecks() {
		if (!this.fields.length) {
			throw new Error('Form: There must be at least one field!');
		}

		if (this.fields.some((field) => !(field instanceof FormField))) {
			throw new TypeError('Form: Wrong type of field!');
		}

		if (typeof this.data !== 'object') {
			throw new TypeError('Form: Wrong type of initial form data - must be an object!');
		}

		if (!this.uri) {
			throw new Error('Form: URI of sending address is not defined!');
		}
	}

	addEventListeners() {
		/* Field Event Listeners */
		this.fields.forEach((field) => {
			field.subscribe('statusChange', this.handleFieldStatusChange.bind(this));

			field.subscribe('valueChange', (event) => {
				this.handleFieldValueChangeEvent(field, event);
			});
		});

		/* Form Event Listeners */
		if (this.subscriptions.length) {
			this.subscriptions.forEach(({ field, event, callback }) => {
				if (typeof event !== 'string') {
					throw new TypeError('Form: Subscription event name must be a string.');
				}

				if (typeof callback !== 'function') {
					throw new TypeError('Form: Subscription callback must be a function.');
				}

				/* Field Events Config Subscriptions */
				if (field) {
					try {
						this.subscribeToFieldEvent(field, event, callback);
					} catch (err) {
						console.error('Subscribe to field event failed.', err);
					}
				} else {
					this.subscribe(event, callback);
				}
			});
		}
	}

	subscribeToFieldEvent(fieldName, event, callback) {
		const field = this.fields.find((field) => fieldName === field.name);

		/* Don't subscribe if there is no field with such name */
		if (!field) {
			return;
		}

		field.subscribe(event, callback);
	}

	handleFieldStatusChange() {
		this.validate();
	}

	handleConsultationFieldValueChange({ detail }) {
		if (detail) {
			this.defaultData.type = `${this.defaultData.type}-consultation`;
		} else {
			const consultationChunkIndex = this.defaultData.type.indexOf('-consultation');
			if (consultationChunkIndex > 0) {
				this.defaultData.type = this.defaultData.type.slice(0, consultationChunkIndex);
			}
		}
	}

	handleFieldValueChangeEvent(field, event) {
		if (field.name === 'getConsultation' && field.switch) {
			this.handleConsultationFieldValueChange(event);
		}

		if (field.name !== 'agreement') {
			this.dispatchEvent(this.FIELD_VALUE_CHANGE_EVENT, { name: field.name, value: event.detail });
		}
	}

	handleSubmit(e) {
		e.preventDefault();
		this.submit();
	}

	handleSecondarySubmit(e) {
		e.preventDefault();
		this.submit(true);
	}

	validate() {
		if (!this.submitButton) {
			return;
		}

		if (this.fields.every((field) => (field.required ? (field.isValid || field.hidden) : true))) {
			this.submitButton.enable();
			this.submitButton.subscribe('click', this.handleSubmit);
		} else {
			this.submitButton.disable();
			this.submitButton.unsubscribe('click', this.handleSubmit);
		}
	}

	getField(name) {
		return this.fields.find((field) => field.name === name);
	}

	/* Submit Works */
	submit(secondary = false) {
		const submitButton = secondary ? this.secondarySubmitButton : this.submitButton;
		this.submitButton.disable();

		if (this.secondarySubmitButton) {
			this.secondarySubmitButton.disable();
		}

		this.collectData();

		if (submitButton.data) {
			Object.assign(this.data, submitButton.data);
		}

		this.dispatchEvent(this.BEFORE_SUBMIT_EVENT, this.data);

		/* Tel input patch if somehow tel data has been came empty */
		if (this.data.hasOwnProperty('tel') && !this.data.tel && !secondary) {
			const telField = this.getField('tel');
			telField.setInitialState();
			telField.validateStrict();
			return;
		}

		const responsePromise = this.sendData();

		if (this.submitConf.animation) {
			let submitStatusCallback = null;

			if (this.waitResponse) {
				submitStatusCallback = () => this.submitStatus;
			}

			submitButton.startAnimation(submitStatusCallback).then(() => {
				this.completeSubmit();
			});
		} else {
			responsePromise.then(() => {
				this.completeSubmit();
			});
		}
	}

	collectData() {
		this.data = Object.assign({}, this.defaultData);

		this.fields.forEach((field) => {
			if (field.sendable && !field.hidden) {
				const fieldData = field.getData();

				if (fieldData !== null) {
					this.data = Object.assign({}, this.data, fieldData);
				}
			}
		});
	}

	sendData() {
		if (!Object.keys(this.data).length) {
			throw new Error('Form: Data object is empty');
		}

		this.submitStatus = null;

		return Api.post(`${this.baseUrl || Api.restSiteUrl}${this.uri}`, this.data)
			.then((res) => {
				this.submitStatus = 'ok';
				this.submitResponse = res;
				this.dispatchEvent(this.SUBMIT_SUCCESS_EVENT, this.submitResponse);
				this.dispatchEvent(this.DATA_SENT_EVENT, this.submitResponse);
			}).catch((err) => {
				this.submitStatus = 'error';
				this.submitResponse = err;
				this.dispatchEvent(this.SUBMIT_FAILURE_EVENT, this.submitResponse);
				this.dispatchEvent(this.DATA_SENT_EVENT, this.submitResponse);
			});
	}

	addData(data) {
		if (typeof data !== 'object') {
			throw new Error('Form: Wrong type of data in addData.');
		}

		const props = Object.keys(data);

		if (!props.length) {
			return;
		}

		props.forEach((prop) => {
			if (typeof prop !== 'string') {
				throw new TypeError(`Form / id: ${this.formId}: Add data failed. Data props must be of string type`);
			}
		});

		this.data = Object.assign({}, this.data, data);
	}

	completeSubmit() {
		this.dispatchEvent(this.SUBMIT_COMPLETE_EVENT, this.submitResponse);

		if (!this.showResult) {
			return;
		}

		if (this.waitResponse) {
			if (this.submitStatus === 'ok') {
				this.handleSubmitSuccess();
			} else if (this.submitStatus === 'error') {
				this.handleSubmitFailure();
			} else {
				const agreementField = this.getField('agreement');
				if (agreementField) {
					agreementField.setInitialState();
				}
			}
		} else {
			this.handleSubmitResult();
		}
	}

	/* Submit Results */
	handleSubmitResult() {
		if (this.cacheEnabled) {
			this.cacheValues();
		}

		if (this.getScreen('result')) {
			this.showScreen('result');
		} else if (this.layout === 'modal' && this.shouldClose) {
			this.serviceLocator.modalService.close();
		}

		/* Wait until animation completes */
		setTimeout(() => {
			if (this.resetAfterSubmit) {
				this.clearForm();
			} else {
				this.validate();
			}
			this.dispatchEvent(this.SUBMIT_FINISH_EVENT);
		}, 360);

	}

	handleSubmitSuccess() {
		if (this.cacheEnabled) {
			this.cacheValues();
		}

		if (this.getScreen('success')) {
			this.showScreen('success');
		} else if (this.getScreen('result')) {
			this.showScreen('result');
		} else if (this.layout === 'modal' && this.shouldClose) {
			this.serviceLocator.modalService.close();
		}

		/* Wait until animation completes */
		setTimeout(() => {
			if (this.resetAfterSubmit) {
				this.clearForm();
			} else {
				this.validate();
			}
			this.dispatchEvent(this.SUBMIT_FINISH_EVENT);
		}, 360);
	}

	handleSubmitFailure() {
		console.error(`Form Submit (id: "${this.formId}"):`, this.submitResponse);

		if (this.getScreen('failure')) {
			this.showScreen('failure');
		} else if (this.getScreen('result')) {
			this.showScreen('result');
		} else if (this.layout === 'modal' && this.shouldClose) {
			this.serviceLocator.modalService.close();
		}

		/* Wait until animation completes */
		setTimeout(() => {
			if (this.resetAfterSubmit) {
				this.clearForm();
			} else {
				this.validate();
			}
			this.dispatchEvent(this.SUBMIT_FINISH_EVENT);
		}, 360);
	}

	cacheValues() {
		let storage = costorage.get('h_formValues');
		storage = storage ? JSON.parse(storage) : {};

		this.fields.forEach((field) => {
			if (field.cacheable) {
				storage[field.name] = field.value;
			}
		});

		costorage.set('h_formValues', JSON.stringify(storage), { expires: 30 * 24 * 60 * 60, path: '/', domain: '.ithillel.ua' })
	}

	clearForm() {
		this.fields.forEach((field) => {
			field.setInitialState();
		});

		if (this.stepsModule) {
			this.stepsModule.setInitialState();
		}
	}

	setFidCid() {
		let cookie = document.cookie.match('(?:^|;)\\s*_ga=([^;]*)');
		const value = (cookie) ? decodeURIComponent(cookie[1]) : null;
		if (value) {
			cookie = value.match(/(\d+\.\d+)$/);
		}
		const cid = (cookie) ? cookie[1] : null;
		let fid = costorage.get('fid') || null;

		if (fid == null) {
			fid = 'f' + Math.floor((Math.random() * 1000000000) + 1) + '.' + Math.round(Date.now() / 1000)
			costorage.set('fid', fid, {expires: 30, path: '/', domain: craft.env.isInternational ? '.ithillel.com' : '.ithillel.ua' });
		}

		this.defaultData.cid = cid;
		this.defaultData.fid = fid;
	}

	getScreen(name) {
		return this.screens.find((screen) => screen.name === name);
	}

	/* Style manipulations */
	showScreen(name = 'layout') {
		if (name === 'layout') {
			this.$root.removeAttribute('data-active-screen');
			this.$formLayout.classList.remove('-hidden');

			this.screens.forEach((screen) => {
				screen.$element.classList.remove('-active');
			});
		} else {
			const screen = this.screens.find((screen) => screen.name === name);

			if (!screen) {
				console.error(`Screen with name "${name}" not found`);
				return;
			}

			this.screens.forEach((screen) => {
				screen.$element.classList.remove('-active');
			});

			this.$formLayout.classList.remove('-hidden');

			let layoutHeight = this.$formLayout.offsetHeight;

			this.$root.classList.add('-hide-layout');

			setTimeout(() => {
				screen.$element.style.height = layoutHeight + 'px';
				screen.$element.classList.add('-active');

				this.$root.dataset.activeScreen = name;
				this.$root.classList.remove('-hide-layout');
				this.$formLayout.classList.add('-hidden');
			}, 5);
		}
	}
}